import * as React from "react";

import { useFormatCurrency } from "@krea/shared/hooks/useFormatCurrency";
import { Currency } from "@krea/shared/types/common";
import { cn } from "@krea/shared/utils";

import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "./input-group";
import { useNumericInput } from "./use-numeric-input";

// Maps each currency to the locale whose number formatting conventions should
// be used (thousands separator style, decimal separator, etc.).
const CURRENCY_LOCALE: Record<Currency, string> = {
  [Currency.SEK]: "sv", // Swedish: "1 234 567"  (space thousands, comma decimal)
  [Currency.EUR]: "fi", // Finnish: "1 234 567"  (space thousands, comma decimal)
};

interface IMoneyInputProps
  extends Omit<
    React.ComponentPropsWithoutRef<"input">,
    "onChange" | "value" | "onBlur"
  > {
  onChange?: (value: number | null) => void;
  value?: number | null;
  onBlur?: (value: number | null) => void;
  currency: Currency;
  /**
   * Large "hero" variant: the amount renders as a big figure on a
   * focus-reactive underline (matches the emphasized loan-application slider
   * input). Left-aligned, hugging the value.
   */
  emphasized?: boolean;
  /**
   * Value size for the emphasized variant. `lg` (default) is the large
   * amount-step figure; `md` is the modest in-flow size used on the
   * multi-field question steps (factoring / leasing). No effect unless
   * `emphasized`.
   */
  emphasizedSize?: "md" | "lg";
}

const MoneyInput = ({
  onChange,
  value,
  onBlur,
  onFocus,
  currency,
  emphasized = false,
  emphasizedSize = "lg",
  ref,
  ...props
}: IMoneyInputProps & {
  ref?: React.Ref<HTMLInputElement>;
}) => {
  const [focused, setFocused] = React.useState(false);
  // Local handle so the currency suffix can focus the input; the forwarded
  // ref still resolves to the same element, in both branches below.
  const innerRef = React.useRef<HTMLInputElement>(null);
  React.useImperativeHandle(ref, () => innerRef.current as HTMLInputElement);
  // Currency symbol ("kr", "€", etc.) — uses app locale from useFormatCurrency
  const [, calculatedCurrency] = useFormatCurrency(CURRENCY_LOCALE[currency], {
    currency,
  });

  // Number formatter cached per currency — recreated only when currency changes
  const currencyFormatter = React.useMemo(
    () =>
      new Intl.NumberFormat(CURRENCY_LOCALE[currency], {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    [currency],
  );

  const format = React.useCallback(
    (val: number) => currencyFormatter.format(val),
    [currencyFormatter],
  );

  const { displayValue, handleFocus, handleChange, handleBlur } =
    useNumericInput({
      value,
      onChange,
      onBlur,
      onFocus,
      type: "integer",
      format,
    });

  if (emphasized) {
    return (
      <div
        className={cn(
          "flex items-baseline border-b-2 pb-1 transition-colors duration-200",
          // Left-aligned, hugging the value. `md` uses `w-fit!` to beat the
          // `*:w-full` a parent `<Field>` forces on its children so the line
          // sits under the input, not the whole row.
          emphasizedSize === "md" ? "w-fit! self-start" : "w-fit",
          focused ? "border-primary" : "border-gray-300",
        )}
      >
        <input
          ref={innerRef}
          value={displayValue}
          onFocus={(e) => {
            setFocused(true);
            handleFocus(e);
          }}
          onChange={handleChange}
          onBlur={(e) => {
            setFocused(false);
            handleBlur(e);
          }}
          inputMode="numeric"
          {...props}
          className={cn(
            "min-w-0 max-w-full appearance-none border-0 bg-transparent px-0 py-0 text-left font-sans font-bold leading-none tracking-tight text-black shadow-none outline-hidden field-sizing-content placeholder:font-normal placeholder:text-gray-500 hover:border-0 focus-visible:border-0",
            emphasizedSize === "md" ? "text-2xl" : "text-4xl",
            props.className,
          )}
        />
        {calculatedCurrency && (
          // Clicking the currency focuses the amount — same affordance as
          // EmphasizedInput's suffix. preventDefault stops mousedown from
          // blurring the input first, which would reformat mid-edit.
          // biome-ignore lint/a11y/noStaticElementInteractions: pointer-only convenience — the input is the focus target and stays keyboard-accessible, so this must not be a tab stop or announce a role
          <span
            onMouseDown={(e) => {
              e.preventDefault();
              innerRef.current?.focus();
            }}
            className={cn(
              "flex shrink-0 cursor-text select-none items-baseline whitespace-nowrap pl-2 font-sans font-semibold leading-none text-black",
              emphasizedSize === "md" ? "text-lg" : "text-2xl",
            )}
          >
            {calculatedCurrency}
          </span>
        )}
      </div>
    );
  }

  return (
    <InputGroup>
      <InputGroupInput
        ref={innerRef}
        value={displayValue}
        onFocus={handleFocus}
        onChange={handleChange}
        onBlur={handleBlur}
        inputMode="numeric"
        {...props}
      />
      {calculatedCurrency && (
        <InputGroupAddon align="inline-end">
          <InputGroupText>{calculatedCurrency}</InputGroupText>
        </InputGroupAddon>
      )}
    </InputGroup>
  );
};

MoneyInput.displayName = "MoneyInput";

export type { IMoneyInputProps };
export { MoneyInput };
